import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalized } from '../../i18n/getLocalized';
import MultiLangInput from '../../components/MultiLangInput/MultiLangInput';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, horizontalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  RiAddLine, RiCloseLine, RiEyeLine, RiEyeOffLine, RiDraggable,
} from 'react-icons/ri';
import { createOption } from './utils';

// ─── Sortable chip (select options) ───────────────────────────────────────
function OptionChip({ option, onUpdate, onRemove }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const shown = getLocalized(option.label, lang);
  const [editing, setEditing] = useState(!shown);
  const inputRef = useRef(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: option.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => setEditing(false);
  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { commit(); }
  };

  return (
    <div ref={setNodeRef} style={style} className="fm-chip">
      <span className="fm-chip__grab" {...attributes} {...listeners} title={t('fieldsManager.fieldRow.drag')}>
        <RiDraggable />
      </span>
      {editing ? (
        <MultiLangInput
          value={option.label}
          onChange={(v) => onUpdate(option.id, v)}
          primaryLang={lang}
          placeholder={t('fieldsManager.optionsEditor.optionPlaceholder')}
          className="fm-ml fm-ml--chip"
          inputClassName="fm-chip__input"
        />
      ) : (
        <span className="fm-chip__text" onClick={() => setEditing(true)} title={t('fieldsManager.optionsEditor.clickToRename')}>
          {shown || <em>{t('fieldsManager.optionsEditor.optionFallback')}</em>}
        </span>
      )}
      <button type="button" className="fm-chip__close" onClick={() => onRemove(option.id)} title={t('fieldsManager.optionsEditor.remove')}>
        <RiCloseLine />
      </button>
    </div>
  );
}

// ─── Standard Dropdown Options Editor (compact chips) ─────────────────────
export default function OptionsEditor({ options, onChange, error }) {
  const { t } = useTranslation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = options.findIndex((o) => o.id === active.id);
    const newIdx = options.findIndex((o) => o.id === over.id);
    onChange(arrayMove(options, oldIdx, newIdx));
  };

  const addOption = () => onChange([...options, createOption('')]);
  const removeOption = (id) => onChange(options.filter((o) => o.id !== id));
  const updateOption = (id, label) => onChange(options.map((o) => (o.id === id ? { ...o, label } : o)));

  return (
    <div className="fm-chips">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={options.map((o) => o.id)} strategy={horizontalListSortingStrategy}>
          {options.map((opt) => (
            <OptionChip key={opt.id} option={opt} onUpdate={updateOption} onRemove={removeOption} />
          ))}
        </SortableContext>
      </DndContext>
      <button type="button" className="fm-chips__add" onClick={addOption}>
        <RiAddLine /> {t('fieldsManager.optionsEditor.addOption')}
      </button>
      {error && <span className="fm-error-text fm-chips__err">{error}</span>}
    </div>
  );
}

// ─── Radio option chip (with visibility rule popover) ─────────────────────
function RadioChip({ option, allFields, onUpdate, onRemove }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const shown = getLocalized(option.label, lang);
  const [editingLabel, setEditingLabel] = useState(!shown);
  const [rulePopover, setRulePopover] = useState(false);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => { if (editingLabel) inputRef.current?.focus(); }, [editingLabel]);
  useEffect(() => {
    if (!rulePopover) return;
    const h = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setRulePopover(false);
        // If user closed without picking any fields, drop the empty rule
        if (option.visibilityRule && !(option.visibilityRule.fieldIds || []).length) {
          onUpdate(option.id, { ...option, visibilityRule: null });
        }
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [rulePopover, option, onUpdate]);

  const hasRule = !!(option.visibilityRule && option.visibilityRule.fieldIds?.length);
  const ruleCount = option.visibilityRule?.fieldIds?.length || 0;

  const updateLabel = (label) => onUpdate(option.id, { ...option, label });
  const commit = () => setEditingLabel(false);

  const toggleRule = () => {
    if (hasRule) {
      onUpdate(option.id, { ...option, visibilityRule: null });
    } else {
      onUpdate(option.id, { ...option, visibilityRule: { action: 'show', fieldIds: [] } });
      setRulePopover(true);
    }
  };

  const updateRulePatch = (patch) => {
    const current = option.visibilityRule || { action: 'show', fieldIds: [] };
    onUpdate(option.id, { ...option, visibilityRule: { ...current, ...patch } });
  };

  const toggleFieldId = (fieldId) => {
    const current = option.visibilityRule || { action: 'show', fieldIds: [] };
    const ids = current.fieldIds || [];
    const next = ids.includes(fieldId) ? ids.filter((i) => i !== fieldId) : [...ids, fieldId];
    onUpdate(option.id, { ...option, visibilityRule: { ...current, fieldIds: next } });
  };

  const rule = option.visibilityRule || { action: 'show', fieldIds: [] };

  return (
    <div className={`fm-radio-chip ${hasRule ? 'has-rule' : ''}`}>
      <span className="fm-radio-chip__bullet" />
      {editingLabel ? (
        <MultiLangInput
          value={option.label}
          onChange={(v) => updateLabel(v)}
          primaryLang={lang}
          placeholder={t('fieldsManager.optionsEditor.optionPlaceholder')}
          className="fm-ml fm-ml--chip"
          inputClassName="fm-radio-chip__input"
        />
      ) : (
        <span className="fm-radio-chip__text" onClick={() => setEditingLabel(true)}>
          {shown || <em>{t('fieldsManager.optionsEditor.optionFallback')}</em>}
        </span>
      )}

      {/* Rule toggle button */}
      <div className="fm-radio-chip__rule-wrap" ref={popoverRef}>
        <button
          type="button"
          className={`fm-radio-chip__rule-btn ${hasRule ? 'is-on' : ''}`}
          onClick={hasRule ? () => setRulePopover((v) => !v) : toggleRule}
          title={hasRule ? `${rule.action} ${ruleCount} field(s)` : t('fieldsManager.optionsEditor.addRule')}
        >
          {hasRule ? <RiEyeLine /> : <RiEyeOffLine />}
          {hasRule && <span className="fm-radio-chip__rule-count">{ruleCount}</span>}
        </button>

        {rulePopover && option.visibilityRule && (
          <div className="fm-rule-popover">
            <div className="fm-rule-popover__head">
              <span className="fm-rule-popover__title">When selected…</span>
              <button type="button" className="fm-rule-popover__remove" onClick={toggleRule}>{t('fieldsManager.optionsEditor.removeRule')}</button>
            </div>
            <div className="fm-rule-popover__seg">
              <button
                type="button"
                className={rule.action === 'show' ? 'is-on' : ''}
                onClick={() => updateRulePatch({ action: 'show' })}
              >Show</button>
              <button
                type="button"
                className={rule.action === 'hide' ? 'is-on' : ''}
                onClick={() => updateRulePatch({ action: 'hide' })}
              >Hide</button>
            </div>
            {allFields.length === 0 ? (
              <p className="fm-rule-popover__empty">No other fields in this section yet.</p>
            ) : (
              <div className="fm-rule-popover__fields">
                {allFields.map((f) => {
                  const on = (rule.fieldIds || []).includes(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      className={`fm-rule-popover__field ${on ? 'is-on' : ''}`}
                      onClick={() => toggleFieldId(f.id)}
                    >
                      <span className="fm-rule-popover__check">{on ? '✓' : ''}</span>
                      <span>{getLocalized(f.label, i18n.language) || <em>{t('fieldsManager.optionsEditor.unnamedField')}</em>}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <button type="button" className="fm-radio-chip__close" onClick={() => onRemove(option.id)} title={t('fieldsManager.optionsEditor.remove')}>
        <RiCloseLine />
      </button>
    </div>
  );
}

// ─── Default (nothing-selected) rule button ───────────────────────────────
function DefaultRuleButton({ defaultRule, onChange, allFields }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasRule = !!(defaultRule && defaultRule.fieldIds?.length);
  const count = defaultRule?.fieldIds?.length || 0;

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        // If user closed without picking any fields, drop the empty rule
        if (defaultRule && !(defaultRule.fieldIds || []).length) {
          onChange(null);
        }
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, defaultRule, onChange]);

  const rule = defaultRule || { action: 'show', fieldIds: [] };

  const toggle = () => {
    if (hasRule) { onChange(null); setOpen(false); }
    else { onChange({ action: 'show', fieldIds: [] }); setOpen(true); }
  };

  const updatePatch = (patch) => onChange({ ...rule, ...patch });
  const toggleFieldId = (fieldId) => {
    const ids = rule.fieldIds || [];
    const next = ids.includes(fieldId) ? ids.filter((i) => i !== fieldId) : [...ids, fieldId];
    onChange({ ...rule, fieldIds: next });
  };

  return (
    <div className="fm-radio-chip__rule-wrap" ref={ref}>
      <button
        type="button"
        className={`fm-radio-chip fm-radio-chip--default ${hasRule ? 'has-rule' : ''}`}
        onClick={hasRule ? () => setOpen((v) => !v) : toggle}
        title={t('fieldsManager.optionsEditor.ruleWhenNothing')}
      >
        <span className="fm-radio-chip__bullet fm-radio-chip__bullet--empty" />
        <span className="fm-radio-chip__text">Default</span>
        {hasRule && <span className="fm-radio-chip__rule-count">{count}</span>}
        {!hasRule && <span className="fm-radio-chip__add-hint">+ rule</span>}
      </button>

      {open && defaultRule && (
        <div className="fm-rule-popover">
          <div className="fm-rule-popover__head">
            <span className="fm-rule-popover__title">When nothing is selected…</span>
            <button type="button" className="fm-rule-popover__remove" onClick={toggle}>{t('fieldsManager.optionsEditor.removeRule')}</button>
          </div>
          <div className="fm-rule-popover__seg">
            <button type="button" className={rule.action === 'show' ? 'is-on' : ''} onClick={() => updatePatch({ action: 'show' })}>Show</button>
            <button type="button" className={rule.action === 'hide' ? 'is-on' : ''} onClick={() => updatePatch({ action: 'hide' })}>Hide</button>
          </div>
          {allFields.length === 0 ? (
            <p className="fm-rule-popover__empty">No other fields in this section yet.</p>
          ) : (
            <div className="fm-rule-popover__fields">
              {allFields.map((f) => {
                const on = (rule.fieldIds || []).includes(f.id);
                return (
                  <button
                    key={f.id}
                    type="button"
                    className={`fm-rule-popover__field ${on ? 'is-on' : ''}`}
                    onClick={() => toggleFieldId(f.id)}
                  >
                    <span className="fm-rule-popover__check">{on ? '✓' : ''}</span>
                    <span>{getLocalized(f.label, i18n.language) || <em>{t('fieldsManager.optionsEditor.unnamedField')}</em>}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function RadioOptionsEditor({ options, onChange, error, allFields, defaultRule, onDefaultRuleChange }) {
  const { t } = useTranslation();
  const addOption = () => onChange([...options, { ...createOption(''), visibilityRule: null }]);
  const removeOption = (id) => onChange(options.filter((o) => o.id !== id));
  const updateOption = (id, updated) => onChange(options.map((o) => (o.id === id ? updated : o)));

  return (
    <div className="fm-radio-chips">
      {options.map((opt) => (
        <RadioChip
          key={opt.id}
          option={opt}
          allFields={allFields || []}
          onUpdate={updateOption}
          onRemove={removeOption}
        />
      ))}
      <button type="button" className="fm-chips__add fm-chips__add--radio" onClick={addOption}>
        <RiAddLine /> {t('fieldsManager.optionsEditor.addOption')}
      </button>
      {onDefaultRuleChange && (
        <DefaultRuleButton
          defaultRule={defaultRule}
          onChange={onDefaultRuleChange}
          allFields={allFields || []}
        />
      )}
      {error && <span className="fm-error-text fm-chips__err">{error}</span>}
    </div>
  );
}
